import { useState, useCallback, useEffect, useContext, useMemo, useRef } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import { CodeBlock } from '@/types/chat-message'
import { ChatMessage } from '@/types'
import { updateMessage } from '@/db/messages'
import { reconstructContentWithCodeBlocks } from '@/lib/messages'
import { REGEX_NOTE } from '@/lib/preview'
import {
  isCanvasClosedForChat,
  setCanvasClosedForChat
} from '@/lib/canvas-state-store'

function compareCodeBlocks(codeBlock1: CodeBlock | null, codeBlock2: CodeBlock | null): boolean {
  if (!codeBlock1 || !codeBlock2) {
    return false
  }
  return (
    codeBlock1.messageId === codeBlock2.messageId &&
    codeBlock1.sequenceNo === codeBlock2.sequenceNo &&
    codeBlock1.code === codeBlock2.code &&
    codeBlock1.language === codeBlock2.language
  )
}

// Stable identity of a code block (ignores the `code` body, which GROWS on
// every stream chunk). Auto-selection must key on identity, not full value:
// otherwise a streaming code block whose body changes each chunk looks "new"
// every render, so the auto-select effect calls setSelectedCodeBlock on every
// commit -> re-render -> repeat = React #185 (max update depth) infinite loop.
function isSameCodeBlockIdentity(
  codeBlock1: CodeBlock | null,
  codeBlock2: CodeBlock | null
): boolean {
  if (!codeBlock1 || !codeBlock2) {
    return codeBlock1 === codeBlock2
  }
  return (
    codeBlock1.messageId === codeBlock2.messageId &&
    codeBlock1.sequenceNo === codeBlock2.sequenceNo &&
    codeBlock1.filename === codeBlock2.filename &&
    codeBlock1.language === codeBlock2.language
  )
}

export function useCodeBlockManager(ignore?: ChatMessage[]) {
  const [selectedCodeBlock, setSelectedCodeBlock] = useState<CodeBlock | null>(null)
  const [selectedMessage, setSelectedMessage] = useState<ChatMessage | null>(null)
  const { isGenerating, chatMessages, setChatMessages, selectedChat } =
    useContext(ChatbotUIChatContext)


  // Sync selectedMessage with chatMessages when chatMessages updates but content hasn't changed
  // This prevents remounts by keeping selectedMessage pointing to the latest object from chatMessages
  useEffect(() => {
    if (!selectedMessage?.message?.id) return

    const currentMessage = chatMessages.find((m) => m.message?.id === selectedMessage.message?.id)
    if (!currentMessage) return

    // Fixpoint: identical reference -> nothing to do. This is the guard that
    // prevents a max-update-depth loop (#185) — as long as the message list
    // keeps stable object identities for unchanged messages, this effect
    // reaches its fixpoint and stops. Functional update avoids a
    // selectedMessage dependency that would otherwise re-arm the effect.
    if (currentMessage === selectedMessage) return

    setSelectedMessage(() => currentMessage)
  }, [chatMessages, selectedMessage?.message?.id])

  const [isEditable, setIsEditable] = useState(false)
  const [manuallyClosedCodeBlock, setManuallyClosedCodeBlock] = useState<CodeBlock | null>(null)
  const [userManuallyClosed, setUserManuallyClosed] = useState(false)
  const [manuallyClosedMessageId, setManuallyClosedMessageId] = useState<string | null>(null)
  const lastMessageCountRef = useRef(chatMessages.length)
  const selectedCodeBlockRef = useRef<CodeBlock | null>(null)
  const previousChatIdRef = useRef<string | null>(null)
  const manualSelectionInProgressRef = useRef(false)

  // Update ref whenever selectedCodeBlock changes
  useEffect(() => {
    selectedCodeBlockRef.current = selectedCodeBlock
  }, [selectedCodeBlock])

  // Reset state when switching chats to fix note persistence across chats
  useEffect(() => {
    const currentChatId = selectedChat?.id || null
    if (previousChatIdRef.current !== currentChatId) {
      // Reset all state when switching chats
      setSelectedCodeBlock(null)
      setSelectedMessage(null)
      setManuallyClosedCodeBlock(null)
      // BUG-01: seed closed-state from localStorage so a canvas the user
      // closed for this chat stays closed across remounts/reloads.
      setUserManuallyClosed(isCanvasClosedForChat(currentChatId))
      setManuallyClosedMessageId(null)
      manualSelectionInProgressRef.current = false
      previousChatIdRef.current = currentChatId
    }
  }, [selectedChat?.id])

  const lastCodeBlock = useMemo(() => {
    if (chatMessages.length === 0) {
      return null
    }

    for (let i = chatMessages.length - 1; i >= 0; i--) {
      const message = chatMessages[i]
      const codeBlocks = message?.codeBlocks
      if (codeBlocks && codeBlocks.length > 0) {
        // Only return code blocks that have filenames (previewable content)
        for (let j = codeBlocks.length - 1; j >= 0; j--) {
          const block = codeBlocks[j]
          if (block.filename) {
            return block
          }
        }
      }
    }

    return null
  }, [chatMessages])

  const handleSelectCodeBlock = useCallback(
    (codeBlock: CodeBlock | null) => {
      setSelectedCodeBlock(codeBlock)
      setIsEditable(compareCodeBlocks(codeBlock, lastCodeBlock))

      // Track manual close actions - use ref to get current value
      if (codeBlock === null && selectedCodeBlockRef.current) {
        if (isGenerating) {
          // During generation: track specific code block
          setManuallyClosedCodeBlock(selectedCodeBlockRef.current)
        } else {
          // After generation: track general manual close
          setUserManuallyClosed(true)
        }
        // BUG-01: persist the closed-state for this chat so it survives
        // remounts when Losi sends the next response.
        setCanvasClosedForChat(selectedChat?.id || null, true)
      } else if (codeBlock !== null) {
        // User opened a code block, reset manual close state
        setUserManuallyClosed(false)
        setCanvasClosedForChat(selectedChat?.id || null, false)
      }
    },
    [lastCodeBlock, isGenerating, selectedChat?.id]
  ) // Removed selectedCodeBlock from dependencies

  const handleSelectMessage = useCallback(
    (message: ChatMessage | null) => {
      // Set flag to prevent auto-selection interference
      manualSelectionInProgressRef.current = true

      setSelectedMessage(message)
      // When selecting a message for editing/preview, clear code block selection
      if (message) {
        setSelectedCodeBlock(null)
        // Opening a preview clears the persisted closed-state for this chat.
        setUserManuallyClosed(false)
        setCanvasClosedForChat(selectedChat?.id || null, false)
        // Only reset manual close tracking for the specific message being selected
        if (message.message?.id) {
          setManuallyClosedMessageId((prev) => (prev === message.message?.id ? null : prev))
        }
      } else {
        // Track manual close of message preview
        if (!isGenerating && selectedMessage?.message?.id) {
          setManuallyClosedMessageId(selectedMessage.message.id)
          setUserManuallyClosed(true)
          setCanvasClosedForChat(selectedChat?.id || null, true)
        }
      }

      // Reset flag after a brief delay to allow this selection to complete
      setTimeout(() => {
        manualSelectionInProgressRef.current = false
      }, 100)
    },
    [isGenerating, selectedMessage, selectedChat?.id]
  )

  const handleCodeChange = useCallback(
    (updatedCode: string, messageId: string, sequenceNo: number): void => {
      const updatedMessage = chatMessages.find((message) => message.message?.id === messageId)

      const codeBlock = updatedMessage?.codeBlocks?.[sequenceNo]

      if (updatedMessage && updatedMessage.codeBlocks && codeBlock) {
        const updatedCodeBlock = {
          ...codeBlock,
          code: updatedCode
        }

        updatedMessage.codeBlocks[sequenceNo] = updatedCodeBlock

        setChatMessages((prev) => {
          const updatedMessages = [...prev]
          const index = updatedMessages.findIndex(
            (message) => message.message?.id === updatedMessage.message?.id
          )
          if (index !== -1) {
            updatedMessages[index] = updatedMessage
          }
          return updatedMessages
        })
        handleSelectCodeBlock(updatedCodeBlock)
        updateMessage(updatedMessage.message!.id, {
          content: reconstructContentWithCodeBlocks(
            updatedMessage.message?.content || '',
            updatedMessage.codeBlocks
          )
        })
      }
    },
    [chatMessages, setChatMessages, handleSelectCodeBlock]
  )

  // Reset manual close tracking when new message starts (new generation)
  // Also detect chat switches by checking if messages array changed dramatically
  useEffect(() => {
    const currentLength = chatMessages.length
    const previousLength = lastMessageCountRef.current

    // If messages array changed dramatically (not just +1), likely a chat switch
    const isDramaticChange = Math.abs(currentLength - previousLength) > 1

    if (isDramaticChange) {
      // Reset all state for chat switch
      setSelectedCodeBlock(null)
      setSelectedMessage(null)
      setManuallyClosedCodeBlock(null)
      // BUG-01: preserve the persisted closed-state for this chat.
      setUserManuallyClosed(isCanvasClosedForChat(selectedChat?.id || null))
      setManuallyClosedMessageId(null)
      manualSelectionInProgressRef.current = false
    } else if (currentLength > previousLength) {
      // Normal new message, only reset specific tracking
      setManuallyClosedCodeBlock(null)
    }

    lastMessageCountRef.current = currentLength
  }, [chatMessages.length])

  // Compute last note-style message (starts with #note=...#)
  // Update immediately when content changes, even during streaming
  const lastNoteMessage = useMemo(() => {
    if (chatMessages.length === 0) return null
    for (let i = chatMessages.length - 1; i >= 0; i--) {
      const m = chatMessages[i]
      const content = m?.message?.content || ''
      if (REGEX_NOTE.test(content)) {
        return m
      }
    }
    return null
  }, [chatMessages, chatMessages[chatMessages.length - 1]?.message?.content])

  // Auto-select code block logic.
  // Keyed on stable IDENTITY (messageId+sequenceNo+filename), NOT full content:
  // a code block being streamed grows its `code` body every chunk, so a
  // value-compare would treat it as "new" on every render and call
  // setSelectedCodeBlock in a commit-time loop (React #185). Identity only
  // changes when a genuinely different block should be auto-opened.
  useEffect(() => {
    if (chatMessages.length === 0) return
    if (!lastCodeBlock) return

    // Same block already selected (even if its streamed body has since grown)
    // -> nothing to auto-select. The content-sync effect below keeps the
    // preview current without re-running this selection logic.
    if (isSameCodeBlockIdentity(selectedCodeBlockRef.current, lastCodeBlock)) {
      return
    }

    const isManuallyClosed =
      manuallyClosedCodeBlock &&
      isSameCodeBlockIdentity(manuallyClosedCodeBlock, lastCodeBlock)

    if ((isGenerating && isManuallyClosed) || userManuallyClosed) {
      // Respect the user's manual close.
      return
    }

    handleSelectCodeBlock(lastCodeBlock)
  }, [
    chatMessages,
    lastCodeBlock,
    manuallyClosedCodeBlock,
    userManuallyClosed,
    isGenerating,
    handleSelectCodeBlock
  ])

  // Keep the SELECTED block's content fresh while it streams, without going
  // through the selection logic above (which would loop). This only updates
  // when the SAME block's body actually changed.
  useEffect(() => {
    if (!lastCodeBlock) return
    const current = selectedCodeBlockRef.current
    if (!current) return
    if (!isSameCodeBlockIdentity(current, lastCodeBlock)) return
    if (current.code === lastCodeBlock.code) return
    setSelectedCodeBlock(lastCodeBlock)
  }, [lastCodeBlock])

  // Auto-select latest note message for preview when applicable
  // Trigger on every content change for real-time streaming updates
  useEffect(() => {
    if (chatMessages.length === 0) return
    if (!lastNoteMessage) return

    // Don't interfere if manual selection is in progress
    if (manualSelectionInProgressRef.current) return

    const alreadySelected = selectedMessage?.message?.id === lastNoteMessage.message?.id
    const wasManuallyClosed = manuallyClosedMessageId === lastNoteMessage.message?.id

    // If generating and user manually closed this message, do not re-open
    if (isGenerating && wasManuallyClosed) return

    // If different from selected and not manually closed, select it
    // During streaming, content updates happen naturally via chatMessages prop updates
    // The stable key prop prevents remounts while allowing re-renders with new content
    if (!alreadySelected && !wasManuallyClosed && !userManuallyClosed) {
      setSelectedMessage(lastNoteMessage)
    }
    // Don't force re-render when already selected - let React handle prop updates naturally
    // The component will receive updated content via selectedMessage prop when chatMessages changes
  }, [
    chatMessages,
    lastNoteMessage,
    selectedMessage,
    manuallyClosedMessageId,
    isGenerating,
    userManuallyClosed,
    chatMessages[chatMessages.length - 1]?.message?.content
  ])

  const handleMessageContentChange = useCallback(
    async (
      updatedContent: string,
      messageId: string,
      previewMode: 'markdown' | 'rich' | 'hybrid' = 'markdown'
    ): Promise<void> => {
      const message = chatMessages.find((m) => m.message?.id === messageId)

      if (message?.message) {
        // Update annotation with editable content
        const currentAnnotation = (message.message.annotation as any) || {}
        const editableContent = {
          originalContent: message.message.content,
          editedContent: updatedContent,
          lastEdited: new Date().toISOString(),
          previewMode,
          isActive: true,
          editHistory: [
            ...(currentAnnotation.editableContent?.editHistory || []),
            {
              content: updatedContent,
              timestamp: new Date().toISOString(),
              version: (currentAnnotation.editableContent?.editHistory?.length || 0) + 1
            }
          ]
        }

        const updatedAnnotation = {
          ...currentAnnotation,
          editableContent
        }

        // Update message in database
        await updateMessage(messageId, {
          annotation: updatedAnnotation
        })

        // Update local state (don't call setState during render)
        setChatMessages((prev) => {
          const updatedMessages = [...prev]
          const index = updatedMessages.findIndex((m) => m.message?.id === messageId)
          if (index !== -1 && updatedMessages[index].message) {
            // Create new message object to avoid mutating the original
            const updatedMessage = {
              ...updatedMessages[index],
              message: {
                ...updatedMessages[index].message!,
                annotation: updatedAnnotation
              }
            }
            updatedMessages[index] = updatedMessage
          }
          return updatedMessages
        })
      }
    },
    [chatMessages, setChatMessages, selectedMessage]
  )

  return {
    selectedCodeBlock,
    selectedMessage, // selectedMessage is kept in sync via useEffect to prevent remounts
    handleSelectCodeBlock,
    handleSelectMessage,
    handleCodeChange,
    handleMessageContentChange,
    isEditable,
    lastNoteMessage
  }
}
